import * as React from 'react';
import {createRoot} from 'react-dom/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {Button} from '../components/ui/button';

interface ConfirmOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

function ConfirmDialogComponent({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}) {
  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button onClick={handleConfirm}>{confirmText}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Global confirm dialog service
class ConfirmDialogService {
  private container: HTMLDivElement | null = null;
  private root: ReturnType<typeof createRoot> | null = null;

  private createContainer() {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'confirm-dialog-container';
      document.body.appendChild(this.container);
      this.root = createRoot(this.container);
    }
  }

  private cleanup() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.container) {
      document.body.removeChild(this.container);
      this.container = null;
    }
  }

  confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      this.createContainer();

      const handleConfirm = () => {
        options.onConfirm?.();
        resolve(true);
        this.cleanup();
      };

      const handleCancel = () => {
        options.onCancel?.();
        resolve(false);
        this.cleanup();
      };

      const ConfirmDialog = () => (
        <ConfirmDialogComponent
          open={true}
          onOpenChange={() => {
            handleCancel();
          }}
          title={options.title}
          description={options.description}
          confirmText={options.confirmText}
          cancelText={options.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      );

      this.root!.render(<ConfirmDialog />);
    });
  }
}

// Export singleton instance
export const confirmDialog = new ConfirmDialogService();

// Export the component for direct use
export {ConfirmDialogComponent};
